import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SignupBreakdownChart from 'src/common/components/signup-breakdown/SignupBreakdownChart';
import SignupBreakdownPie from 'src/common/components/signup-breakdown/SignupBreakdownPie';
import SignupBreakdownTable from 'src/common/components/signup-breakdown/SignupBreakdownTable';
import { LanguageKeys } from 'src/common/constants/languages';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { buildSignupBreakdownSeries } from 'src/helpers/signupBreakdown';
import { useSignupBreakdown } from 'src/hooks/useSignupBreakdown';
import { IEdition } from 'src/interfaces/Edition';
import {
  SignupBreakdownGroupBy,
  SignupBreakdownView,
} from 'src/interfaces/SignupBreakdown';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  edition: IEdition;
  chartHeight?: number;
  /** Include unpublished competitions/distances (admin historical views). */
  includeUnpublished?: boolean;
}

const panelSx = {
  ...frostedPanelSx(0.5),
  width: '100%',
  p: 2,
} as const;

const SignupBreakdownSection = ({
  edition,
  chartHeight,
  includeUnpublished = false,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const competitionsById = useSystemOptionsStore(s => s.competitionsById);
  const distancesById = useSystemOptionsStore(s => s.distancesById);

  const [groupBy, setGroupBy] = useState<SignupBreakdownGroupBy>('distance');
  const [view, setView] = useState<SignupBreakdownView>('pie');

  const { items, isLoading, error } = useSignupBreakdown({
    editionId: edition.id,
  });

  const series = useMemo(
    () =>
      buildSignupBreakdownSeries(items, edition, {
        competitionsById,
        distancesById,
        lang: currentLang,
        groupBy,
        includeUnpublished,
      }),
    [
      competitionsById,
      currentLang,
      distancesById,
      edition,
      groupBy,
      includeUnpublished,
      items,
    ],
  );

  const hasData = series.totalCount > 0 && series.labels.length > 0;

  const handleGroupByChange = (
    _event: MouseEvent<HTMLElement>,
    next: SignupBreakdownGroupBy | null,
  ) => {
    if (next) setGroupBy(next);
  };

  const handleViewChange = (
    _event: MouseEvent<HTMLElement>,
    next: SignupBreakdownView | null,
  ) => {
    if (next) setView(next);
  };

  return (
    <Paper sx={panelSx}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" component="h3">
          {groupBy === 'distance'
            ? t('signups.breakdown.titleByDistance')
            : t('signups.breakdown.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={groupBy}
            onChange={handleGroupByChange}
            aria-label={t('signups.breakdown.groupByLabel')}
          >
            <ToggleButton value="competition">
              {t('signups.breakdown.groupByCompetition')}
            </ToggleButton>
            <ToggleButton value="distance">
              {t('signups.breakdown.groupByDistance')}
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={handleViewChange}
            aria-label={t('signups.breakdown.viewLabel')}
          >
            <ToggleButton value="bar">{t('signups.breakdown.viewBar')}</ToggleButton>
            <ToggleButton value="pie">{t('signups.breakdown.viewPie')}</ToggleButton>
            <ToggleButton value="table">{t('signups.breakdown.viewTable')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="text.secondary">
          {t('signups.breakdown.loadError')}
        </Typography>
      ) : !hasData ? (
        <Typography variant="body2" color="text.secondary">
          {t('signups.breakdown.empty')}
        </Typography>
      ) : view === 'bar' ? (
        <SignupBreakdownChart series={series} height={chartHeight} />
      ) : view === 'pie' ? (
        <SignupBreakdownPie series={series} height={chartHeight} />
      ) : (
        <SignupBreakdownTable series={series} groupBy={groupBy} />
      )}
    </Paper>
  );
};

export default SignupBreakdownSection;
